/**
 * The games list: the regions and rooms the backend's /games answers with, refreshed every ten seconds while the
 * lobby or the game selector is open, and the ping to each region. A region is measured once per page load, as the
 * original client did: one random room's host per region, up to four requests in flight, one every 300 ms and up
 * to seven per host, a host settled once two readings in a row agree within ten percent, and the lowest kept. Two
 * things improve on the original's measurement: the first request to a host is a warm-up whose time is discarded,
 * since it pays for the connection, and a host has one request in flight at a time, so every reading rides the
 * warm connection. The port added the refresh, which pings only a region still without a reading, three quick
 * retries when the first fetch comes back empty (iOS Safari's first fetch often fails while networking warms up),
 * a timeout on every request, the answer checked field by field, and generation tokens, since the selector's
 * opening and closing start and stop the service without ref-counting.
 */
import { BACKEND_HOST, DEVELOPMENT } from '../game/Constants';
import type { GameRegion, GameServer } from '../game/types/Games';

/** How often the list is fetched while the service runs. */
const REFRESH_INTERVAL_MS = 10000;
/** The boot's quick retries when the first fetch comes back empty. */
const INITIAL_LOAD_RETRY_DELAYS_MS = [500, 1000, 1500];
/** The list fetch's timeout, so a hung backend cannot hold the boot. */
const GAMES_TIMEOUT_MS = 10000;
const PING_INTERVAL_MS = 300;
const PING_TIMEOUT_MS = 3000;
/** Requests per host in a cycle, the warm-up included. */
const MAX_PINGS_PER_HOST = 7;
const MAX_PINGS_IN_FLIGHT = 4;
/** Two readings this close, as a fraction of the newer, settle a host. */
const PING_AGREEMENT = 0.1;

/** Told the list and the closest region's id after every change. */
export type GamesDataUpdate = (regions: readonly GameRegion[], closestRegionId: string | null) => void;

/** A host measured in the current ping cycle. */
interface HostPing {
  /** The lowest reading so far, Infinity until the first. */
  ping: number;
  /** Requests sent, the warm-up included. */
  sent: number;
  /** Readings taken, for the agreement rule. */
  readings: number;
  /** Whether a request is in flight: one at a time keeps the connection warm. */
  busy: boolean;
  /** The region the host stands for, by id since a refresh may reorder the list. */
  regionId: string;
}

/** The regions and their rooms: a new array after every change, so a mirror sees each one. */
export let regions: readonly GameRegion[] = [];
/** The backend's country for the player, '' until the first answer. */
export let country = '';
/** The protocol version the backend names, 0 until the first answer. */
export let protocol = 0;
/** The region with the lowest ping, null until one has a reading. */
export let closestRegionId: string | null = null;
/** Whether an answer has been parsed. Until then an empty list means the backend has not been reached, not maintenance. */
export let loaded = false;

let listener: GamesDataUpdate | null = null;
let running = false;
/** Bumped by every start and stop, so an awaited step of a start since stopped or superseded gives up. */
let runId = 0;
let refreshTimer: number | null = null;
/** Bumped by every ping cycle's start and stop, so a late response of an old cycle changes nothing. */
let pingRunId = 0;
let pingTimer: number | null = null;
const hosts = new Map<string, HostPing>();
let pingsInFlight = 0;

/** Sets the function told of every change. */
export function onUpdate(update: GamesDataUpdate | null): void {
  listener = update;
}

/** Whether there is nothing to join: no list yet, or every region empty, as during maintenance; `loaded` tells the two apart. */
export function isEmpty(): boolean {
  return regions.length === 0 || regions.every((region) => region.games.length === 0);
}

/**
 * Fetches the list, retries quickly while it comes back empty, then refreshes it every ten seconds until stopped.
 * The boot passes fromMainPage, which the original's main page sent the backend as a query parameter.
 */
export async function start(fromMainPage = false): Promise<void> {
  if (running) return;
  running = true;
  const run = ++runId;
  const stopped = () => !running || run !== runId;

  await refresh(fromMainPage);
  if (stopped()) return;
  for (const delay of INITIAL_LOAD_RETRY_DELAYS_MS) {
    if (!isEmpty()) break;
    await new Promise((resolve) => setTimeout(resolve, delay));
    if (stopped()) return;
    await refresh(fromMainPage);
    if (stopped()) return;
  }
  refreshTimer = window.setInterval(() => void refresh(), REFRESH_INTERVAL_MS);
}

/** Stops the refresh and the pings; the list and the readings stay. */
export function stop(): void {
  runId++;
  running = false;
  stopPinging();
  if (refreshTimer !== null) {
    window.clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function notify(): void {
  listener?.(regions, closestRegionId);
}

/** Fetches the list; a failed or malformed answer leaves the current one. Readings carry over by region id. */
async function refresh(fromMainPage = false): Promise<void> {
  const url = `https://${BACKEND_HOST}/games${fromMainPage ? '?main=1' : ''}`;
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(GAMES_TIMEOUT_MS),
    });
    if (!response.ok) return;
    const answer = parseAnswer(await response.json());
    if (answer === null) return;
    regions = answer.regions.map((region) => {
      const known = regions.find((r) => r.id === region.id);
      return known?.ping == null ? region : { ...region, ping: known.ping };
    });
    if (answer.country !== undefined) country = answer.country;
    if (answer.protocol !== undefined) protocol = answer.protocol;
    loaded = true;
    recomputeClosest();
    notify();
    // A region without a reading yet, new or one whose host failed, gets its measurement
    if (running && pingTimer === null) startPinging();
  } catch (error) {
    // Silent in production as the original was; in dev the symptom, a blank lobby, is invisible otherwise
    if (DEVELOPMENT) console.warn('[GamesData] refresh failed:', error);
  }
}

/** The answer's fields the client reads, checked one by one; null for anything else. */
function parseAnswer(value: unknown): { regions: GameRegion[]; country?: string; protocol?: number } | null {
  if (!isRecord(value)) return null;
  const { data, country: countryField, protocol: protocolField } = value;
  if (typeof data !== 'string') return null;
  let list: unknown;
  try {
    list = JSON.parse(data);
  } catch {
    return null;
  }
  if (!Array.isArray(list)) return null;
  const parsed: GameRegion[] = [];
  for (const entry of list as unknown[]) {
    const region = toRegion(entry);
    if (region === null) return null;
    parsed.push(region);
  }
  return {
    regions: parsed,
    country: typeof countryField === 'string' ? countryField : undefined,
    protocol: typeof protocolField === 'number' ? protocolField : undefined,
  };
}

function toRegion(value: unknown): GameRegion | null {
  if (!isRecord(value)) return null;
  const { id, name, games: rooms } = value;
  if (typeof id !== 'string' || typeof name !== 'string' || !Array.isArray(rooms)) return null;
  const games: GameServer[] = [];
  for (const entry of rooms as unknown[]) {
    const game = toGame(entry);
    if (game === null) return null;
    games.push(game);
  }
  return { id, name, games };
}

function toGame(value: unknown): GameServer | null {
  if (!isRecord(value)) return null;
  const { type, id, name, nameShort, host, path, players, bots } = value;
  if (
    typeof type !== 'string' ||
    typeof id !== 'string' ||
    typeof name !== 'string' ||
    typeof nameShort !== 'string' ||
    typeof host !== 'string' ||
    typeof path !== 'string'
  ) {
    return null;
  }
  if ((players !== undefined && typeof players !== 'number') || (bots !== undefined && typeof bots !== 'number')) {
    return null;
  }
  return { type, id, name, nameShort, host, path, players, bots };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Starts a cycle for the regions without a reading, one random room's host each; nothing to do ends it at once. */
function startPinging(): void {
  stopPinging();
  const cycle = pingRunId;
  hosts.clear();
  pingsInFlight = 0;
  for (const region of regions) {
    if (region.ping != null || region.games.length === 0) continue;
    const host = region.games[Math.floor(Math.random() * region.games.length)]!.host;
    if (!hosts.has(host)) hosts.set(host, { ping: Infinity, sent: 0, readings: 0, busy: false, regionId: region.id });
  }
  if (hosts.size === 0) return;
  pingTimer = window.setInterval(() => sendPing(cycle), PING_INTERVAL_MS);
  sendPing(cycle);
  sendPing(cycle);
  sendPing(cycle);
}

/** Ends the cycle; a late response of it then changes nothing. */
function stopPinging(): void {
  pingRunId++;
  if (pingTimer !== null) {
    window.clearInterval(pingTimer);
    pingTimer = null;
  }
}

/** Sends the cycle's next request, to the free host with the fewest sent; the cycle ends once every host has had its share. */
function sendPing(cycle: number): void {
  if (cycle !== pingRunId || pingsInFlight >= MAX_PINGS_IN_FLIGHT) return;
  let candidate: string | null = null;
  let candidateState: HostPing | null = null;
  let fewestSent = Infinity;
  for (const [host, state] of hosts) {
    fewestSent = Math.min(fewestSent, state.sent);
    if (!state.busy && (candidateState === null || state.sent < candidateState.sent)) {
      candidate = host;
      candidateState = state;
    }
  }
  if (fewestSent >= MAX_PINGS_PER_HOST) {
    stopPinging();
    return;
  }
  // Every free host has had its share; the busy ones finish on their own
  if (candidate === null || candidateState === null || candidateState.sent >= MAX_PINGS_PER_HOST) return;
  candidateState.sent++;
  void measure(candidate, candidateState, cycle, true);
}

/** One request to the host: the first a warm-up, every later one a reading taken by the original's rule. */
async function measure(host: string, state: HostPing, cycle: number, retryOnImprovement: boolean): Promise<void> {
  state.busy = true;
  pingsInFlight++;
  const started = performance.now();
  let reached = false;
  try {
    // no-cors as the original sent it: any answer counts, and a server need not allow the origin
    await fetch(`https://${host}/ping`, {
      mode: 'no-cors',
      cache: 'no-cache',
      signal: AbortSignal.timeout(PING_TIMEOUT_MS),
    });
    reached = true;
  } catch (error) {
    // Timeout or network failure: the host is dropped for this cycle; a refresh tries the region again
    if (cycle === pingRunId) {
      hosts.delete(host);
      if (DEVELOPMENT) console.warn(`[GamesData] ping ${host} failed:`, error);
    }
  } finally {
    state.busy = false;
    if (cycle === pingRunId) pingsInFlight--;
  }
  if (!reached || cycle !== pingRunId || hosts.get(host) !== state) return;
  // The warm-up paid for the connection; its time says nothing about the distance
  if (state.sent === 1) return;
  const ping = performance.now() - started;
  state.readings++;
  if (Math.abs(state.ping - ping) < PING_AGREEMENT * ping && state.readings >= 2) {
    // Settled: a lower reading still counts, otherwise the host is done
    if (ping < state.ping) recordPing(host, state, ping);
    else hosts.delete(host);
  } else if (ping < state.ping) {
    state.ping = ping;
    recordPing(host, state, ping);
    // An improving host is asked again at once, as the original did, once
    if (retryOnImprovement) void measure(host, state, cycle, false);
  }
}

/** Writes a reading onto the host's region and recomputes the closest; a region gone from the list drops the host. */
function recordPing(host: string, state: HostPing, ping: number): void {
  if (!regions.some((region) => region.id === state.regionId)) {
    hosts.delete(host);
    return;
  }
  const rounded = Math.round(ping);
  // A new array, so the UI's mirror sees the change: Valtio ignores an assignment of the same reference
  regions = regions.map((region) => (region.id === state.regionId ? { ...region, ping: rounded } : region));
  recomputeClosest();
  notify();
}

/** The region with the lowest reading, the first on a tie; null without any. */
function recomputeClosest(): void {
  let lowest = Infinity;
  closestRegionId = null;
  for (const region of regions) {
    if (region.ping != null && region.ping < lowest) {
      lowest = region.ping;
      closestRegionId = region.id;
    }
  }
}
